import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { AuthLayout } from './-components/auth/auth-layout'
import { useLingui } from '@lingui/react/macro'
import { ImageCompany } from '@/routes/_app/-components/assets/image-company'
import { ImageApplicant } from '@/routes/_app/-components/assets/image-applicant'
import { Frame } from '@/components/ui/frame'
import { HomeLink } from '@/routes/_app/-components/shared/home-link'
import { ArrowRightIcon } from 'lucide-react'
import * as v from 'valibot'
import { RegisterChoicePending } from './-components/shared/flow-pending'

export const Route = createFileRoute('/_app/register/')({
  validateSearch: v.object({ redirect: v.optional(v.string()) }),
  component: RouteComponent,
  pendingComponent: RegisterChoicePending,
})

function RouteComponent() {
  const { t } = useLingui()
  // Carry the post-auth destination (e.g. the profile a visitor wanted to compare against) into the
  // chosen registration branch so it survives the whole onboarding.
  const { redirect } = Route.useSearch()

  return (
    <AuthLayout>
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-8 p-6">
        <HomeLink />
        <div className="flow-header text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-balance">{t`Create your account`}</h1>
          <p className="text-muted-foreground mt-2 text-pretty">{t`Choose your option to register with D'Anchiano`}</p>
        </div>

        <div className="flow-content flex w-full flex-col gap-4 sm:flex-row">
          <ChoiceCard
            to="/register/company"
            redirect={redirect}
            title={t`Companies`}
            description={t`Evaluate and manage talent for your organization`}
            icon={<ImageCompany className="size-7" fill="rgb(var(--color-primary))" />}
          />
          <ChoiceCard
            to="/register/applicant"
            redirect={redirect}
            title={t`Professionals`}
            description={t`Discover your psychographic profile and your market fit`}
            icon={<ImageApplicant className="size-7" fill="rgb(var(--color-primary))" width={28} height={28} />}
          />
        </div>

        <p className="text-muted-foreground text-sm">
          {t`Already have an account?`}{' '}
          <Link to="/login" search={{ redirect }} className="text-primary font-medium hover:underline">
            {t`Login`}
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}

/** One of the two doors, answering hover as the landing's solution cards do: the tile grows, the ground stays put. */
function ChoiceCard(props: {
  to: '/register/company' | '/register/applicant'
  redirect?: string
  title: string
  description: string
  icon: ReactNode
}) {
  const { t } = useLingui()

  return (
    <Link to={props.to} search={{ redirect: props.redirect }} className="group flex-1">
      <Frame className="h-full">
        <div className="flex flex-col items-center gap-4 px-4 py-2 text-center">
          <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full transition-transform duration-300 ease-out group-hover:scale-110 group-focus-visible:scale-110">
            {props.icon}
          </div>
          <div>
            <h2 className="text-lg font-semibold">{props.title}</h2>
            <p className="text-muted-foreground mt-1 text-sm">{props.description}</p>
          </div>
          <span className="text-primary inline-flex items-center gap-1 text-sm font-medium">
            {t`Get started`}
            <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </Frame>
    </Link>
  )
}
